import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useUser } from '@/contexts/UserContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeToMeta = {
  trial_ending:  { icon: 'solar:wallet-money-linear', color: 'amber' },
  ticket_update: { icon: 'solar:chat-round-dots-linear', color: 'blue' },
};

const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = user?.user_id;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const readKey = `bh_read_notifs_${userId}`;
  const historyKey = `bh_notif_history_${userId}`;

  useEffect(() => {
    if (!userId) return;

    const fetchAll = async () => {
      setLoading(true);

      // 1. Fetch server notifications (all, read + unread)
      let serverNotifs = [];
      try {
        const res = await axios.get(`${API}/notifications/${userId}/all?limit=100`);
        serverNotifs = (res.data || []).map(n => {
          const meta = typeToMeta[n.type] || { icon: 'solar:bell-linear', color: 'stone' };
          return {
            id: n.notification_id,
            type: n.type,
            icon: meta.icon,
            color: meta.color,
            title: n.title,
            message: n.message,
            timestamp: n.created_at,
            status: n.read ? 'read' : 'active',
          };
        });
      } catch (e) {
        console.debug('Failed to fetch server notification history', e?.message);
      }

      // 2. Load localStorage notifications (client-generated ones like reminders, check-ins)
      let localNotifs = [];
      try {
        const stored = JSON.parse(localStorage.getItem(historyKey) || '[]');
        const readIds = JSON.parse(localStorage.getItem(readKey) || '[]');
        localNotifs = stored.map(n => ({
          ...n,
          status: readIds.includes(n.id) ? 'read' : (n.status || 'active')
        }));
      } catch { /* ignore */ }

      // 3. Merge: server notifications take priority, then local-only ones
      const byId = new Map();
      serverNotifs.forEach(n => byId.set(n.id, n));
      localNotifs.forEach(n => { if (!byId.has(n.id)) byId.set(n.id, n); });

      const merged = Array.from(byId.values())
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setHistory(merged);
      setLoading(false);
    };

    fetchAll();
  }, [historyKey, readKey, userId]);

  const clearHistory = () => {
    localStorage.setItem(historyKey, '[]');
    setHistory([]);
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'active': return { label: t('notifications.statusActive', 'Active'), color: '#4ade80' };
      case 'read': return { label: t('notifications.statusRead', 'Read'), color: '#a8a29e' };
      case 'done': return { label: t('notifications.statusDone', 'Done'), color: '#60a5fa' };
      default: return { label: status, color: '#a8a29e' };
    }
  };

  const iconColors = {
    amber:  { background: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
    indigo: { background: 'rgba(99,102,241,0.15)', color: '#818cf8' },
    red:    { background: 'rgba(239,68,68,0.15)',   color: '#f87171' },
    orange: { background: 'rgba(249,115,22,0.15)',  color: '#fb923c' },
    blue:   { background: 'rgba(59,130,246,0.15)',   color: '#60a5fa' },
    green:  { background: 'rgba(34,197,94,0.15)',    color: '#4ade80' },
    stone:  { background: 'var(--c-surface-card)',  color: '#a8a29e' },
  };

  // Group by date
  const grouped = {};
  history.forEach(n => {
    const date = n.timestamp ? new Date(n.timestamp).toLocaleDateString() : t('notifications.unknown', 'Unknown');
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(n);
  });

  return (
    <div className="min-h-screen" style={{ background: 'var(--c-surface)' }}>
      {/* Header */}
      <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between" style={{ background: 'var(--c-surface)', borderBottom: '1px solid var(--c-border)' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'var(--c-surface-card)' }}
          >
            <Icon icon="solar:arrow-left-linear" width={18} height={18} style={{ color: 'var(--c-texPri)' }} />
          </button>
          <h1 className="text-lg font-semibold" style={{ color: 'var(--c-texPri)' }}>
            {t('notifications.allTitle', 'Notifications')}
          </h1>
        </div>
        {history.length > 0 && (
          <button
            onClick={clearHistory}
            className="text-xs px-3 py-1.5 rounded-full"
            style={{ color: 'var(--c-texSec)', background: 'var(--c-surface-card)' }}
          >
            {t('notifications.clearHistory', 'Clear history')}
          </button>
        )}
      </div>

      {/* List */}
      <div className="pb-24">
        {loading ? (
          <div className="p-12 text-center">
            <p className="text-sm" style={{ color: 'var(--c-texSec)' }}>
              {t('notifications.loading', 'Loading...')}
            </p>
          </div>
        ) : history.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--c-surface-card)' }}>
              <Icon icon="solar:bell-linear" width={40} height={40} style={{ color: 'var(--c-texDis)' }} />
            </div>
            <p className="text-base" style={{ color: 'var(--c-texTer)' }}>
              {t('notifications.noHistory', 'No notification history yet')}
            </p>
          </div>
        ) : (
          Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <div className="px-5 py-2 sticky top-14" style={{ background: 'var(--c-surface)' }}>
                <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--c-texTer)' }}>{date}</p>
              </div>
              {items.map((notification) => {
                const ic = iconColors[notification.color] || iconColors.stone;
                const st = getStatusInfo(notification.status);
                return (
                  <div
                    key={notification.id}
                    className="px-5 py-3 flex items-start gap-3"
                    style={{ borderBottom: '1px solid var(--c-border)' }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: ic.background }}
                    >
                      <Icon icon={notification.icon || 'solar:bell-linear'} width={20} height={20} style={{ color: ic.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm" style={{ color: 'var(--c-texPri)' }}>{notification.title}</p>
                      {notification.message && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--c-texSec)' }}>{notification.message}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs" style={{ color: st.color }}>● {st.label}</span>
                        {notification.timestamp && (
                          <span className="text-xs" style={{ color: 'var(--c-texDis)' }}>
                            {new Date(notification.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
